import React, { memo, useCallback, useMemo } from 'react'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FormattedMessage, useIntl } from 'react-intl'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid2'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import usePageProps from 'hooks/usePageProps'
import InstagramIcon from '@mui/icons-material/Instagram'
import FacebookIcon from '@mui/icons-material/Facebook'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import YoutubeIcon from '@mui/icons-material/YouTube'
import TwitterIcon from '@mui/icons-material/Twitter'
import TiktokIcon from 'public/svg/tiktok.svg'
import AMVOIcon from 'public/img/afiliado-amvo-blanco.png'
import useUtms from 'hooks/useUtms'
import { titleCaps } from 'utils/universal'
import { setOverridedFilters } from 'redux/modules/search'
import { useDispatch } from 'react-redux'
import useMediaQuery from '@mui/material/useMediaQuery'
import { parseFiltersToUrl } from 'filters'
import sx from './styles'

const Footer = (props) => {
  const {
    logo = '',
    logoAltText = '',
    mainMenu = [],
    menu = [],
    makes = [],
    variantPhone,
  } = props
  const {
    siteName, socialLinks, shoppingPhone, salesPhone, contactPhoneDisplay,
  } = usePageProps()
  const router = useRouter()
  const query = useUtms()
  const dispatch = useDispatch()
  const { formatMessage: f } = useIntl()
  const basePath = f({ id: 'ROUTE_SEARCH' })
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const phone = useMemo(() => (variantPhone === 'sales' ? salesPhone : shoppingPhone), [variantPhone])

  const handleRedirectMaker = useCallback((event, maker) => {
    event.preventDefault()
    dispatch(setOverridedFilters({ maker: maker.toLowerCase() }))
    if (router.asPath.includes(f({ id: 'ROUTE_SEARCH' }))) {
      router.push(`/autos-seminuevos/marca-${maker.toLowerCase()}`, null, { shallow: true })
    } else {
      router.push(`/autos-seminuevos/marca-${maker.toLowerCase()}`)
    }
  }, [])

  const makesList = useMemo(() => {
    return makes.map((maker, i) => (
      <Button
        key={`footerlink-${maker.name}`}
        href={`${basePath}${(parseFiltersToUrl({ maker: maker.name })).toLowerCase()}`}
        onClick={(e) => handleRedirectMaker(e, maker.name)}
        variant="text"
        display="inline"
        sx={sx.carList}
        component="a"
        title={`Autos seminuevos ${maker.name}`}
      >
        {i !== 0 && ' | '}
        {titleCaps(maker.name)}
      </Button>
    ))
  }, [makes])

  return (
    <Box
      sx={sx.root}
      id="Footer"
    >
      <Container maxWidth="xl">
        <Grid
          container
          spacing={2}
        >
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
            container
            spacing={2}
          >
            <Grid
              size="auto"
              sx={sx.logo}
            >
              {typeof logo === 'string' ? (
                <Image
                  src={logo}
                  alt={logoAltText}
                  width={200}
                  height={36}
                  layout="responsive"
                />
              ) : <logo />}
            </Grid>

            <Grid size={12}>
              <Typography
                variant="strong"
                sx={sx.footerTitle}
              >
                <FormattedMessage id="FOOTER_LOGO_DESCRIPTION" />
              </Typography>
            </Grid>

            <Grid size={12}>
              <Grid
                size={12}
                container
                spacing={2}
                justifyContent={isTablet ? 'flex-start' : 'space-between'}
              >
                <Grid size="auto">
                  <a
                    href={socialLinks?.facebook}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Facebook"
                  >
                    <FacebookIcon sx={{ color: 'white' }} />
                  </a>
                </Grid>

                <Grid size="auto">
                  <a
                    href={socialLinks?.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Linkedin"
                  >
                    <LinkedInIcon sx={{ color: 'white' }} />
                  </a>
                </Grid>

                <Grid size="auto">
                  <a
                    href={socialLinks?.instagram}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                  >
                    <InstagramIcon sx={{ color: 'white' }} />
                  </a>
                </Grid>

                <Grid size="auto">
                  <a
                    href={socialLinks?.youtube}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Youtube"
                  >
                    <YoutubeIcon sx={{ color: 'white' }} />
                  </a>
                </Grid>

                <Grid size="auto">
                  <a
                    href={socialLinks?.twitter}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Twitter"
                  >
                    <TwitterIcon sx={{ color: 'white' }} />
                  </a>
                </Grid>

                <Grid size="auto">
                  <a
                    href={socialLinks?.tiktok}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Tiktok"
                  >
                    <TiktokIcon style={{ width: 20, height: 20, color: 'white' }} />
                  </a>
                </Grid>
                <Grid size="auto">
                  <Box sx={{ width: 50, height: 20, position: 'relative' }}>
                    {/*
                      Sólo `layout="fill"`: este componente viene de
                      `next/legacy/image`, donde `fill` a secas no existe (es de
                      la API nueva). Al no reconocerlo, lo reenviaba al <img>
                      del DOM y React avisaba: «Received `true` for a
                      non-boolean attribute `fill`».
                    */}
                    <Image
                      src={AMVOIcon}
                      alt="AMVO logo"
                      layout="fill"
                      objectFit="contain"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

          </Grid>

          {/* divider */}
          <Grid
            size={{
              xs: 12,
              md: 1,
            }}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <Divider
              sx={{
                backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.2)' : 'secondary.contrastText', width: isMobile ? '100%' : '1px', height: '90%',
              }}
              orientation={isMobile ? 'horizontal' : 'vertical'}
            />
          </Grid>

          {/* menu */}
          <Grid
            size={{
              xs: 12,
              md: 8,
            }}
            container
            // spacing={2}
            component="ul"
            sx={{ pl: 0, my: 0 }}
          >
            {mainMenu
              .filter((i) => !i.hideInFooter)
              .map((item) => (
                <Grid
                  key={`section-${item.id}`}
                  size={{
                    xs: 12,
                    sm: 4,
                    md: 'grow',
                  }}
                  component="li"
                  sx={sx.menuItem}
                >
                  <Box>
                    <Link
                      href={`${item.url}${query}`}
                      passHref
                    >
                      <Typography
                        variant="h4"
                        component="p"
                        href={`${item?.url}${query}`}
                        id={item.link_id}
                        sx={sx.linktTitle}
                        title={item?.titleFooter ? item.titleFooter : item?.alt}
                      >
                        {item.title}
                      </Typography>
                    </Link>
                    {item.items && item.items.map((subItem) => {
                      return subItem.type === 'external' ? (
                        <Typography
                          variant="link-text"
                          sx={sx.linkItem}
                          title={subItem.title}
                          target="_blank"
                          href={subItem.url}
                          key={`items-${subItem.title}`}
                          component="a"
                        >
                          {subItem.title}
                        </Typography>
                      ) : (
                        <Link
                          key={subItem.id}
                          href={`${subItem.url}${query}`}
                          passHref
                          title={subItem.title}
                        >
                          <Typography
                            variant="link-text"
                            sx={sx.linkItem}
                            title={subItem.title}
                          >
                            {subItem.title}
                          </Typography>
                        </Link>
                      )
                    })}
                  </Box>
                </Grid>
              ))}
          </Grid>

          <Grid
            size={12}
          >
            <Divider sx={{ backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.2)' : 'secondary.contrastText' }} />
          </Grid>

          <Grid
            size={12}
          >
            <Typography
              variant="body1"
              sx={sx.carsForSale}
            >
              <FormattedMessage id="PRODUCT_DESCRIPTION2" />
            </Typography>

            <Box sx={{ lineHeight: '9px' }}>
              {makesList}
              {' | '}
              <Link
                href={f({ id: 'ROUTE_MAKES_SITEMAP' })}
              >
                <Button
                  variant="text"
                  display="inline"
                  sx={sx.carList}
                  aria-label="Sitemap"
                >
                  <FormattedMessage id="SITEMAP" />
                </Button>
              </Link>
            </Box>
          </Grid>

          <Grid
            size={12}
          >
            <Divider sx={{ backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.2)' : 'secondary.contrastText' }} />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Typography
              variant="body2"
              sx={sx.copyright}
            >
              &copy;
              {' '}
              {siteName}
              {' '}
              {new Date().getFullYear()}
              {' '}
              <FormattedMessage id="COPYRIGHT" />
            </Typography>
            <Typography
              variant="body1"
              sx={sx.copyright}
            >
              <FormattedMessage id="ADDRESS" />
            </Typography>

            <a
              href="mailto:hola@clikauto.com"
              style={{ textDecorationColor: 'white' }}
            >
              <Typography
                variant="body1"
                sx={sx.copyright}
              >
                hola@clikauto.com
              </Typography>
            </a>

            <a
              href={`tel:${phone}`}
              style={{ textDecorationColor: 'white' }}
            >
              <Typography
                variant="body1"
                sx={sx.copyright}
              >
                <FormattedMessage
                  id="PHONE_NUMBER"
                  values={{
                    phoneNumber: contactPhoneDisplay,
                  }}
                />
              </Typography>
            </a>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
            container
            spacing={2}
            component="ul"
            sx={sx.rightSections}
          >
            {menu
              ?.filter((i) => i.title !== 'Prensa').map((item) => (
                <Grid
                  key={item.title}
                  size={{
                    xs: 12,
                    md: 'auto',
                  }}
                  component="li"
                  sx={sx.menuItem}

                >
                  <Link
                    href={item.url}
                    passHref
                  >
                    <Typography
                      variant="link-text"
                      sx={sx.bottomLinks}
                    >
                      {item.title}
                    </Typography>
                  </Link>
                </Grid>
              ))}
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default memo(Footer)
